import { useRef, forwardRef, useEffect, useImperativeHandle } from "react";
import { FriendlyCaptchaSDK } from "@friendlycaptcha/sdk";

const sdk = new FriendlyCaptchaSDK({
    apiEndpoint: "global", // "eu" falls du den EU-Endpoint nutzt
    disableEvalPatching: false,
});

const FriendlyCaptcha = forwardRef(function FriendlyCaptcha(props, ref) {
    const captchaRef = useRef(null);
    const widgetRef = useRef(null);

    // Callback-Props von Widget-Optionen trennen
    const {
        onComplete,
        onError,
        onExpire,
        ...widgetOptions
    } = props;

    // Widget erstellen / zerstören
    useEffect(() => {
        if (!captchaRef.current) return;

        widgetRef.current = sdk.createWidget({
            element: captchaRef.current,
            ...widgetOptions,
        });

        return () => {
            if (widgetRef.current) {
                widgetRef.current.destroy();
                widgetRef.current = null;
            }
        };
    }, Object.values(widgetOptions));

    // Event Listener
    useEffect(() => {
        const element = captchaRef.current;
        if (!element) return;

        const handleComplete = (e) => {
            if (onComplete && e?.detail?.response) {
                onComplete(e.detail.response);
            }
        };

        const handleError = (e) => {
            if (onError && e?.detail?.error) {
                onError(e.detail.error);
            }
        };

        if (onComplete)
            element.addEventListener("frc:widget.complete", handleComplete);
        if (onError)
            element.addEventListener("frc:widget.error", handleError);
        if (onExpire)
            element.addEventListener("frc:widget.expire", onExpire);

        return () => {
            if (onComplete)
                element.removeEventListener("frc:widget.complete", handleComplete);
            if (onError)
                element.removeEventListener("frc:widget.error", handleError);
            if (onExpire)
                element.removeEventListener("frc:widget.expire", onExpire);
        };
    }, [onComplete, onError, onExpire]);

    // Imperative API (ref.reset())
    useImperativeHandle(ref, () => ({
        reset() {
            if (widgetRef.current)
                widgetRef.current.reset();
        },
    }));

    return <div ref={captchaRef} data-start="auto" />;
});

export default FriendlyCaptcha;
